import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Eye, CheckCircle, XCircle, AlertCircle, Filter,
  ChevronLeft, ChevronRight, MapPin, Star, Calendar, Bed,
} from 'lucide-react';
import apiService from '../../apiService';
import { useLiveRefresh } from '../../liveRefresh';
import Img from '../../components/Img';

// ─── Config statuts de modération ─────────────────────────────────────────────
const MOD_STATUS = {
  published_pending_review: { label: 'En révision',   bg: '#fef9c3', color: '#ca8a04', icon: AlertCircle },
  verified:                 { label: 'Vérifiée',      bg: '#ECFDF5', color: '#10B981', icon: CheckCircle },
  flagged:                  { label: 'Signalée',      bg: '#ffedd5', color: '#ea580c', icon: AlertCircle },
  rejected:                 { label: 'Retirée',       bg: '#fecaca', color: '#dc2626', icon: XCircle },
};

const LODGING_TYPE_LABELS = {
  hotel:       '🏨 Hôtel',
  apartment:   '🏠 Appartement',
  villa:       '🏡 Villa',
  studio:      '🛏️ Studio',
  guest_house: '🏘️ Maison d\'hôtes',
  other:       '🏗️ Autre',
};

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ─── Composant badge statut ───────────────────────────────────────────────────
function StatusBadge({ status }) {
  const cfg = MOD_STATUS[status] || MOD_STATUS.published_pending_review;
  const Icon = cfg.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      background: cfg.bg, color: cfg.color,
      padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
    }}>
      <Icon size={12} /> {cfg.label}
    </span>
  );
}

// ─── Modal détail annonce ─────────────────────────────────────────────────────
function LodgingModal({ lodging, onClose, onModerate }) {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!lodging) return null;

  const handleAction = async (action) => {
    setSubmitting(true);
    try {
      await onModerate(lodging.lodging_id, action, note);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
    }}>
      <div style={{
        background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '680px',
        maxHeight: '90vh', overflow: 'auto', padding: '28px',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700 }}>{lodging.name}</h2>
            <p style={{ margin: '4px 0 0', color: '#5C636E', fontSize: '14px' }}>
              {LODGING_TYPE_LABELS[lodging.type] || lodging.type} · {lodging.city}
              {lodging.neighborhood ? ` — ${lodging.neighborhood}` : ''}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5C636E', fontSize: '22px' }}>✕</button>
        </div>

        {/* Statut actuel */}
        <div style={{ marginBottom: '16px' }}>
          <StatusBadge status={lodging.moderation_status} />
          {lodging.moderation_note && (
            <p style={{ marginTop: '8px', color: '#DC2626', fontSize: '13px', padding: '8px 12px', background: '#fef2f2', borderRadius: '8px' }}>
              Note précédente : {lodging.moderation_note}
            </p>
          )}
        </div>

        {/* Photo */}
        {lodging.cover_image && (
          <Img src={lodging.cover_image} alt="" style={{ width: '100%', height: '180px', objectFit: 'cover', borderRadius: '12px', marginBottom: '16px' }} />
        )}

        {/* Infos de base */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
          <InfoBox label="Adresse" value={lodging.address} />
          <InfoBox label="Fournisseur" value={lodging.supplier_id} />
          <InfoBox label="Types de chambres" value={`${lodging.room_types?.length || 0} type(s)`} />
          <InfoBox label="Unités physiques" value={`${(lodging.room_types || []).reduce((s, rt) => s + (rt.units?.length || 0), 0)} chambre(s)`} />
          <InfoBox label="Politique annulation" value={lodging.cancellation_policy} />
          <InfoBox label="Paiement" value={lodging.payment_options === 'on_site_allowed' ? 'Sur place accepté' : 'En ligne uniquement'} />
          <InfoBox label="Check-in" value={lodging.check_in_time || '14:00'} />
          <InfoBox label="Check-out" value={lodging.check_out_time || '11:00'} />
        </div>

        {/* Description */}
        {lodging.description && (
          <div style={{ marginBottom: '16px', padding: '12px', background: '#FFFFFF', borderRadius: '8px' }}>
            <p style={{ margin: 0, fontSize: '13px', color: '#414852', lineHeight: 1.6 }}>{lodging.description}</p>
          </div>
        )}

        {/* Équipements */}
        {lodging.amenities?.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <p style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: 600, color: '#414852' }}>Équipements établissement</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {lodging.amenities.map(a => (
                <span key={a} style={{ background: '#FEE9E2', color: '#C9350F', padding: '3px 10px', borderRadius: '10px', fontSize: '12px' }}>{a}</span>
              ))}
            </div>
          </div>
        )}

        {/* Types de chambres */}
        {lodging.room_types?.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600, color: '#414852' }}>Types de chambres</p>
            {lodging.room_types.map(rt => (
              <div key={rt.room_type_id} style={{ background: '#FFFFFF', borderRadius: '8px', padding: '10px', marginBottom: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 700, fontSize: '14px' }}>{rt.name}</span>
                  <span style={{ fontSize: '13px', color: '#10B981', fontWeight: 600 }}>
                    {rt.price_per_night?.toLocaleString('fr-FR')} CDF/nuit
                  </span>
                </div>
                <span style={{ fontSize: '12px', color: '#5C636E' }}>
                  {rt.units?.length || 0} unité(s) · max {rt.max_guests} pers.
                </span>
                {rt.amenities?.length > 0 && (
                  <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#9AA0A8' }}>{rt.amenities.join(', ')}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Règles */}
        {lodging.house_rules?.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <p style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: 600, color: '#414852' }}>Règles de la maison</p>
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              {lodging.house_rules.map((r, i) => <li key={i} style={{ fontSize: '13px', color: '#5C636E' }}>{r}</li>)}
            </ul>
          </div>
        )}

        {/* Zone de décision modération */}
        <div style={{ borderTop: '1px solid #E9EBEF', paddingTop: '16px', marginTop: '8px' }}>
          <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600 }}>Note de modération (optionnel)</p>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Raison du refus ou du signalement..."
            rows={3}
            style={{
              width: '100%', borderRadius: '8px', border: '1px solid #E9EBEF',
              padding: '10px 12px', fontSize: '13px', resize: 'vertical',
              fontFamily: 'inherit', boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', gap: '10px', marginTop: '12px', flexWrap: 'wrap' }}>
            <ActionButton
              label="✅ Vérifier"
              color="#10B981" bg="#ECFDF5" hoverBg="#bbf7d0"
              disabled={submitting}
              onClick={() => handleAction('verify')}
            />
            <ActionButton
              label="⚠️ Signaler"
              color="#ea580c" bg="#ffedd5" hoverBg="#fed7aa"
              disabled={submitting}
              onClick={() => handleAction('flag')}
            />
            <ActionButton
              label="✗ Rejeter"
              color="#dc2626" bg="#fecaca" hoverBg="#fca5a5"
              disabled={submitting || !note.trim()}
              title={!note.trim() ? 'Ajoutez une note de rejet' : ''}
              onClick={() => handleAction('reject')}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoBox({ label, value }) {
  return (
    <div style={{ background: '#FFFFFF', borderRadius: '8px', padding: '10px 12px' }}>
      <p style={{ margin: 0, fontSize: '11px', color: '#9AA0A8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</p>
      <p style={{ margin: '3px 0 0', fontSize: '13px', fontWeight: 600, color: '#414852' }}>{value || '—'}</p>
    </div>
  );
}

function ActionButton({ label, color, bg, hoverBg, onClick, disabled, title }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: hover && !disabled ? hoverBg : bg, color,
        border: 'none', borderRadius: '8px', padding: '10px 18px',
        cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '13px',
        opacity: disabled ? 0.6 : 1, transition: 'background 0.15s',
      }}
    >
      {label}
    </button>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────
export default function LodgingModerationPage() {
  const [lodgings, setLodgings] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [modFilter, setModFilter] = useState('published_pending_review');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [viewLodging, setViewLodging] = useState(null);
  const limit = 15;

  const fetchLodgings = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page, limit, status: modFilter });
      if (search.trim()) qs.set('search', search.trim());
      if (dateFrom) qs.set('created_from', dateFrom);
      if (dateTo) qs.set('created_to', dateTo);
      if (supplierFilter.trim()) qs.set('supplier_id', supplierFilter.trim());
      const res = await apiService.get(`/lodgings/admin/moderation?${qs}`, { userType: 'admin' });
      setLodgings(res.lodgings || []);
      setTotal(res.total || 0);
    } catch (e) {
      console.error('fetchLodgings admin:', e);
    } finally {
      setLoading(false);
    }
  }, [page, search, modFilter, dateFrom, dateTo, supplierFilter]);

  useEffect(() => { fetchLodgings(); }, [fetchLodgings]);
  useLiveRefresh(() => fetchLodgings(), ['lodging_booking']);
  useEffect(() => { setPage(1); }, [search, modFilter, dateFrom, dateTo, supplierFilter]);

  // L'UI utilise des verbes (verify/flag/reject) ; le backend attend le statut cible.
  const ACTION_TO_STATUS = { verify: 'verified', flag: 'flagged', reject: 'rejected' };
  const handleModerate = async (lodgingId, action, note) => {
    await apiService.patch(`/lodgings/admin/${lodgingId}/moderate`, {
      moderation_status: ACTION_TO_STATUS[action] || action,
      moderation_note: note,
    }, { userType: 'admin' });
    fetchLodgings();
  };

  const pages = Math.ceil(total / limit);

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 800, margin: 0, color: '#12151A' }}>
          🏨 Modération — Hôtel &amp; Appartement
        </h1>
        <p style={{ margin: '4px 0 0', color: '#5C636E', fontSize: '14px' }}>
          Vérification a posteriori des annonces d'hébergement publiées
        </p>
      </div>

      {/* Filtres */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
          <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9AA0A8' }} />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher par nom, ville..."
            style={{
              width: '100%', padding: '9px 12px 9px 34px', borderRadius: '10px',
              border: '1px solid #E9EBEF', fontSize: '14px', outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>
        {/* Filtre statut modération */}
        <select
          value={modFilter}
          onChange={e => setModFilter(e.target.value)}
          style={{ padding: '9px 12px', borderRadius: '10px', border: '1px solid #E9EBEF', fontSize: '14px', cursor: 'pointer' }}
        >
          <option value="">Tous les statuts</option>
          {Object.entries(MOD_STATUS).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        {/* Filtre fournisseur (ID) */}
        <input
          type="text"
          value={supplierFilter}
          onChange={e => setSupplierFilter(e.target.value)}
          placeholder="ID fournisseur"
          style={{ padding: '9px 12px', borderRadius: '10px', border: '1px solid #E9EBEF', fontSize: '14px', minWidth: '150px' }}
        />
        {/* Plage de dates de création */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '13px', color: '#5C636E' }}>Du</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: '10px', border: '1px solid #E9EBEF', fontSize: '13px' }} />
          <span style={{ fontSize: '13px', color: '#5C636E' }}>au</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: '10px', border: '1px solid #E9EBEF', fontSize: '13px' }} />
        </div>
        {(dateFrom || dateTo || supplierFilter) && (
          <button
            onClick={() => { setDateFrom(''); setDateTo(''); setSupplierFilter(''); }}
            style={{ padding: '9px 12px', borderRadius: '10px', border: '1px solid #E9EBEF', background: '#FFFFFF', cursor: 'pointer', fontSize: '13px', color: '#5C636E' }}
          >
            Réinitialiser
          </button>
        )}
      </div>

      {/* Stats rapides */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {Object.entries(MOD_STATUS).map(([k, v]) => {
          const Icon = v.icon;
          return (
            <button
              key={k}
              onClick={() => setModFilter(k === modFilter ? '' : k)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: modFilter === k ? v.bg : '#FFFFFF',
                color: modFilter === k ? v.color : '#5C636E',
                border: modFilter === k ? `1.5px solid ${v.color}` : '1px solid #E9EBEF',
                borderRadius: '10px', padding: '8px 14px', cursor: 'pointer',
                fontSize: '13px', fontWeight: 600, transition: 'all 0.15s',
              }}
            >
              <Icon size={14} /> {v.label}
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: '14px', overflow: 'hidden', border: '1px solid #E9EBEF' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#FFFFFF', borderBottom: '1px solid #E9EBEF' }}>
              {['Annonce', 'Type / Ville', 'Chambres', 'Statut', 'Publiée le', 'Actions'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 700, color: '#9AA0A8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: '48px', color: '#9AA0A8' }}>Chargement...</td></tr>
            ) : lodgings.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: '48px', color: '#9AA0A8' }}>
                Aucune annonce {modFilter ? `avec le statut "${MOD_STATUS[modFilter]?.label}"` : ''}
              </td></tr>
            ) : lodgings.map(l => (
              <tr key={l.lodging_id} style={{ borderBottom: '1px solid #F4F5F7', transition: 'background 0.1s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#FFFFFF'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
              >
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ fontWeight: 700, fontSize: '14px', color: '#12151A', maxWidth: '200px' }} className="truncate">{l.name}</div>
                  <div style={{ fontSize: '12px', color: '#9AA0A8' }}>#{l.lodging_id?.slice(-8)}</div>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ fontSize: '13px' }}>{LODGING_TYPE_LABELS[l.type] || l.type}</div>
                  <div style={{ fontSize: '12px', color: '#5C636E', display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <MapPin size={11} /> {l.city}
                  </div>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Bed size={14} /> {l.room_types?.length || 0} type(s)
                  </div>
                  <div style={{ fontSize: '12px', color: '#5C636E' }}>
                    {(l.room_types || []).reduce((s, rt) => s + (rt.units?.length || 0), 0)} unité(s)
                  </div>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <StatusBadge status={l.moderation_status} />
                </td>
                <td style={{ padding: '12px 16px', fontSize: '13px', color: '#5C636E' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Calendar size={12} /> {formatDate(l.created_at)}
                  </div>
                  {l.average_rating > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '12px', color: '#FFB900', marginTop: '2px' }}>
                      <Star size={11} fill="#FFB900" /> {l.average_rating.toFixed(1)} ({l.review_count})
                    </div>
                  )}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <button
                    onClick={() => setViewLodging(l)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '5px',
                      background: '#FEE9E2', color: '#C9350F', border: '1px solid #bae6fd',
                      borderRadius: '8px', padding: '7px 12px', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                    }}
                  >
                    <Eye size={14} /> Examiner
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            style={{ background: page > 1 ? '#F4F5F7' : '#FFFFFF', border: 'none', borderRadius: '8px', padding: '8px 12px', cursor: page > 1 ? 'pointer' : 'default', color: '#414852' }}
          >
            <ChevronLeft size={16} />
          </button>
          <span style={{ fontSize: '14px', color: '#414852' }}>Page {page} / {pages} ({total} annonce{total > 1 ? 's' : ''})</span>
          <button
            onClick={() => setPage(p => Math.min(pages, p + 1))}
            disabled={page >= pages}
            style={{ background: page < pages ? '#F4F5F7' : '#FFFFFF', border: 'none', borderRadius: '8px', padding: '8px 12px', cursor: page < pages ? 'pointer' : 'default', color: '#414852' }}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Modal détail */}
      {viewLodging && (
        <LodgingModal
          lodging={viewLodging}
          onClose={() => setViewLodging(null)}
          onModerate={handleModerate}
        />
      )}
    </div>
  );
}
